import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, ShieldAlert, Phone } from 'lucide-react';

const SuspendedScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 to-red-50 dark:from-zinc-950 dark:to-zinc-900 p-4">
    <div className="w-full max-w-md text-center">
      <div className="font-extrabold text-3xl text-zinc-900 dark:text-white tracking-tight mb-8">
        DAMATT <span className="text-[#2563eb]">SYSTEMS</span>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-red-200 dark:border-red-900 rounded-2xl p-8 shadow-xl">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/60 flex items-center justify-center">
            <ShieldAlert className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">Conta suspensa</h2>
        <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-6">
          O acesso ao sistema está temporariamente suspenso. Entre em contato com o suporte da Damatt Systems para regularizar sua conta.
        </p>
        <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
          <Phone className="w-4 h-4" />
          <span>Suporte: <a href="https://wa.me/5585996612947" className="text-[#2563eb] font-semibold">(85) 99661-2947</a></span>
        </div>
      </div>
    </div>
  </div>
);

const ProtectedRoute = ({ children, roles, modules, permission }) => {
  const { user, loading, suspended, hasModule, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
      </div>
    );
  }

  if (suspended) return <SuspendedScreen />;

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  if ((permission && !hasPermission(permission, roles)) || (!permission && roles && !roles.includes(user.role))) {
    const isSuperAdminRoute = !permission && roles?.length === 1 && roles[0] === 'superadmin';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-6"
        data-testid={isSuperAdminRoute ? 'superadmin-forbidden' : 'access-denied'}
      >
        <div className="text-center">
          <h1 className="text-xl font-bold text-zinc-900 dark:text-white">Acesso negado</h1>
          <p className="text-zinc-600 dark:text-zinc-400 mt-2">Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  if (modules && !modules.some((m) => hasModule(m))) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-6"
        data-testid="module-forbidden"
      >
        <div className="text-center">
          <h1 className="text-xl font-bold text-zinc-900 dark:text-white">Módulo não contratado</h1>
          <p className="text-zinc-600 dark:text-zinc-400 mt-2">Este recurso não está incluso no plano atual.</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
